import type {
  Challenge,
  ChallengeResponses,
  User,
  UserChallenge,
  UserInformation,
} from "@/interfaces/Auth";
import type { RemovableRef } from "@vueuse/core";

import router from "@/router";

import { autologin, login, validateLogin } from "@/services/auth.services";
import {
  getLatestDocuments,
  checkSignature,
} from "@/services/legal-documents.services";
import { defineStore } from "pinia";
import { useStorage } from "@vueuse/core";
import { useProductStore } from "./productStore";
import type { ProductDetail } from "@/interfaces/product.interface";
import {
  AUTOLOGIN_FEATURE,
  getAppConfig,
  isFeatureEnabled,
} from "@/config/app-config";

interface AuthState {
  currentStep: number;
  userChallenge: UserChallenge;
  token: RemovableRef<string | null>;
  user: RemovableRef<User | null>;
  showMessage: boolean;
  showCloseSesion: boolean;
  error: boolean;
  documentHashes: string[];
  allDocumentsSigned: RemovableRef<boolean>;
  showComplianceDialog: boolean;
}

const handleError = (e: any) => {
  throw e;
};

export const useAuthStore = defineStore("auth", {
  state: (): AuthState => ({
    currentStep: 0,
    userChallenge: {} as UserChallenge,
    token: useStorage("token", null),
    user: useStorage("user", {} as User),
    showMessage: false,
    showCloseSesion: true,
    error: false,
    documentHashes: [],
    allDocumentsSigned: useStorage("allDocumentsSigned", false),
    showComplianceDialog: false,
  }),

  getters: {
    answers(state): ChallengeResponses[] {
      return state.userChallenge.challenge || [];
    },
    organizationClientId: () => {
      return getAppConfig().organizationId;
    },
  },
  actions: {
    async fetchLatestDocumentHashes(): Promise<void> {
      try {
        const data = await getLatestDocuments("GO");
        this.documentHashes = data.documents.map((d) => d.file_hash);
      } catch (error) {
        console.error("Error fetching latest documents:", error);
      }
    },

    async checkUserDocumentSignature(userId: string | number): Promise<void> {
      try {
        const data = await checkSignature(userId, "GO");
        const docs = data.documents;
        this.allDocumentsSigned = data.all_signed;

        if (this.user) {
          this.user.all_signed = data.all_signed;
          this.user.already_signed = docs.some((d) => d.already_signed);
          this.user.has_signed_tyc =
            docs.find((d) => d.code === "TYC")?.signed ?? false;
          this.user.has_signed_pp =
            docs.find((d) => d.code === "PP")?.signed ?? false;
        }
      } catch (error) {
        console.error("Error checking signature:", error);
        this.allDocumentsSigned = false;
      }
    },

    async sendChallenge(challenge: Challenge): Promise<void> {
      try {
        const response = await login(challenge);
        this.userChallenge = response.data;
        this.currentStep++;
      } catch (error) {
        handleError(error);
      }
    },

    async validateChallenge(user: UserInformation): Promise<void> {
      try {
        const response = await validateLogin(user);
        this.user = response.data;
        this.token = response.data.access_token;
        localStorage.removeItem("sesionExpired");

        if (getAppConfig().tycEnabled) {
          if (this.user?.id) {
            await this.checkUserDocumentSignature(this.user.user_id);
          }

          if (!this.allDocumentsSigned) {
            await this.fetchLatestDocumentHashes();
            this.showComplianceDialog = true;
            router.push({ name: "Login" });
            return;
          }
        }

        router.push({ name: "Inicio" });
      } catch (error) {
        handleError(error);
      }
    },

    async automaticLogin(search: string): Promise<void> {
      if (this.error) {
        return;
      }

      const urlParams = new URLSearchParams(search);
      if (!urlParams.has("key")) {
        return;
      }

      if (!isFeatureEnabled(AUTOLOGIN_FEATURE)) {
        this.error = true;
        this.showCloseSesion = false;
        return;
      }

      try {
        this.showCloseSesion = false;
        const keyValue = urlParams.get("key");

        const { data } = await autologin({
          organization_id: getAppConfig().organizationId,
          key: keyValue,
        });

        this.user = data;
        this.token = data.access_token;
        localStorage.removeItem("sesionExpired");

        const hasActiveRequest = data.has_active_request;

        const productStore = useProductStore();

        const productsWithoutPolicies = data.product.map((product) => {
          const { polizas, ...rest } = product;
          return rest;
        });

        productStore.products =
          productsWithoutPolicies as unknown as typeof productStore.products;

        const currentProduct = data.product.reduce((acc, curr) => {
          const searchPolicyById = curr.polizas.find(
            (policy) => policy.id === data.poliza_id
          );

          return searchPolicyById ? curr : ({} as ProductDetail);
        });

        const { polizas, ...product } = currentProduct ?? {};
        productStore.setCurrentProduct(product);
        productStore.setPolicies(polizas);

        if (getAppConfig().tycEnabled) {
          if (this.user?.id) {
            await this.checkUserDocumentSignature(this.user.id);
          }

          if (!this.allDocumentsSigned) {
            await this.fetchLatestDocumentHashes();
            this.showComplianceDialog = true;
            router.push({ name: "Login" });
            return;
          }
        }

        if (hasActiveRequest) {
          router.push({ name: "Inicio" });
        } else {
          router.push({
            name: "RequestsCreate",
            query: { policy_id: data?.poliza_id },
          });
        }

        this.showMessage = true;
      } catch (error: any) {
        if (error.status === 401) this.error = true;
        this.showCloseSesion = false;
        handleError(error);
      }
    },
  },
});
